import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  Typography,
  Table,
  Input,
  Space,
  message,
  DatePicker,
  Badge,
  Modal,
  Tooltip,
} from "antd";
import { ExpenseServices } from "../../../../Services/Manage/ExpenseServices";
import { ServicetypeService } from "../../../../Services/Manage/ServicetypeService";
import { ItemService } from "../../../../Services/Manage/ItemService";
import { SearchOutlined, ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import ProExpensePay from "../../../modals/ProExpensePay";



import { UserService } from "../../../../Services/Manage/UserService";


const getPermission = (link) => {
  try {
    const raw = localStorage.getItem("ome_permission");
    const parsed = JSON.parse(raw);
    const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
    return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
  } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ManageProEx = () => {

  const perm = getPermission('/project-expenses');
  const [originalData, setOriginalData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [messageApi, contextHolder] = message.useMessage();
  const [vendorOption, setVendorOption] = useState([]);
  const [itemMap, setItemMap] = useState({});
  const navigate = useNavigate();
  const [projectPaidMap, setProjectPaidMap] = useState({});

  // Payment Modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectGrandTotal, setProjectGrandTotal] = useState(0);



  const viewAll = perm.view_all === true;

  const [currentUid, setCurrentUid] = useState(null);


  // Fetch All Expenses
  const getAllExpense = async () => {
    try {
      const res = await ExpenseServices.getAllExpense();
      const data = res.data || [];
      // Filter only project-type expenses
      let projectExpenses = data.filter(item => item.type === "project");

      if (!viewAll) {
        const user = await UserService.getOmeUser();
        const uid = String(user.uid).trim();
        setCurrentUid(uid);

        projectExpenses = projectExpenses.filter(group => {
          const project = group.project || {};

          let engineers = project.pro_engineer;
          if (typeof engineers === "string") {
            try { engineers = JSON.parse(engineers); } catch { engineers = []; }
          }
          const isEngineer = (engineers || []).some(
            (id) => String(id).trim() === uid
          );

          const ownsProject =
            String(project.pro_manager ?? "").trim() === uid ||
            String(project.added_by ?? "").trim() === uid ||
            isEngineer;

          const addedExpenseHere = (group.data || []).some(
            (exp) => String(exp.added_by ?? "").trim() === uid
          );

          return ownsProject || addedExpenseHere;
        });
      }

      const splitRows = [];
      projectExpenses.forEach((group) => {
        const items = group.data || [];
        const batches = {};
        items.forEach((exp) => {
          // Prefer the exact created timestamp (added_on) as the batch
          // key, since every item from a single "Save Expense" submission
          // shares the same added_on value. Only fall back to
          // reference_no/expense_date/id if added_on isn't present.
          const batchKey = exp.added_on || exp.reference_no || exp.expense_date || exp.id;
          if (!batches[batchKey]) batches[batchKey] = [];
          batches[batchKey].push(exp);
        });
        Object.values(batches).forEach((batchItems) => {
          splitRows.push({ project: group.project, data: batchItems });
        });
      });

      setOriginalData(splitRows);
    } catch (error) {
      console.error(error);
      messageApi.error("Failed to fetch project expenses");
    }
  };

  // Fetch Vendors (for display)
  const GetAllVendors = async () => {
    try {
      const res = await ServicetypeService.getAllVendors();
      setVendorOption(
        res.data.map(item => ({ label: item.name, value: item.id }))
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getAllItems = async () => {
    try {
      const res = await ItemService.getAllItem();
      const map = {};
      (res.data || []).forEach((i) => { map[String(i.id)] = i.item_name; });
      setItemMap(map);
    } catch (error) {
      console.error(error);
    }
  };


  useEffect(() => {
    getAllExpense();
    GetAllVendors();
    getAllItems();
  }, []);

  // Filter Projects by Search & Date
  const filteredProjects = useMemo(() => {
    const [start, end] = dateRange;
    const lowerSearch = searchText.toLowerCase();

    return originalData
      .map(group => {
        const project = group.project;
        const matchesSearch =
          project?.pro_name?.toLowerCase().includes(lowerSearch) ||
          project?.pro_no?.toLowerCase().includes(lowerSearch) ||
          project?.pro_description?.toLowerCase().includes(lowerSearch);

        const filteredItems = group.data.filter(exp => {
          const vendorName = vendorOption.find(v => v.value === exp.vendor_id)?.label || "";
          const desc = (exp.description || "").toLowerCase();
          const itemMatchesSearch = desc.includes(lowerSearch) || vendorName.toLowerCase().includes(lowerSearch);

          const addedOn = exp.added_on ? new Date(exp.added_on) : null;
          const matchesDate = !start || !end || !addedOn || (addedOn >= start && addedOn <= end);

          return itemMatchesSearch && matchesDate;
        });

        if (matchesSearch || filteredItems.length > 0) {
          return { ...group, data: filteredItems };
        }
        return null;
      })
      .filter(Boolean);
  }, [originalData, searchText, dateRange, vendorOption]);

  // Calculate Totals for a Project
  const calculateProjectTotals = (projectGroup) => {
    const items = projectGroup.data || [];

    const grandTotal = items.reduce((sum, item) => {
      const price = Number(item.totalprice) || 0;
      const taxPercent = Number(item.tax) || 0;
      const taxAmount = (price * taxPercent) / 100;
      return sum + price + taxAmount;
    }, 0);

    const paidAmount = items.reduce((sum, item) => {
      if (Array.isArray(item.payments)) {
        return sum + item.payments.reduce((pSum, p) => pSum + Number(p.paid_amt || 0), 0);
      }
      return sum;
    }, 0);

    const pendingAmount = grandTotal - paidAmount;

    let status = "Unpaid";
    if (paidAmount >= grandTotal && grandTotal > 0) status = "Paid";
    else if (paidAmount > 0) status = "Partially Paid";

    return { grandTotal, paidAmount, pendingAmount, status };
  };

  // Pay Now Handler
  const handlePayNow = (record) => {
    const { grandTotal } = calculateProjectTotals(record);

    setProjectGrandTotal(grandTotal);
    setSelectedProject(record);
    setPaymentModalOpen(true);
  };



  const projectColumns = [
    {
      title: "S.No",
      key: "sno",
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Project Name",
      dataIndex: ["project", "pro_name"],
      render: text => <strong>{text || "-"}</strong>,
    },
    {
      title: "Project No.",
      dataIndex: ["project", "pro_no"],
      render: text => <strong>{text || "-"}</strong>,
    },
    {
      title: "Items",
      align: "center",
      render: (_, r) => r.data?.length || 0,
    },
    {
      title: "Grand Total",
      align: "right",
      render: (_, record) => {
        const { grandTotal } = calculateProjectTotals(record);
        return <strong>{grandTotal.toFixed(2)}</strong>;
      },
    },
    {
      title: "Paid",
      align: "right",
      render: (_, record) => {
        const paid =
          projectPaidMap[record.project.pro_no] !== undefined
            ? projectPaidMap[record.project.pro_no]
            : calculateProjectTotals(record).paidAmount;



        return (
          <span style={{ color: "#52c41a" }}>
            {paid.toFixed(2)}
          </span>
        );
      },
    },

    // {
    //   title: "Pending",
    //   align: "right",
    //   render: (_, record) => {
    //     const { pendingAmount } = calculateProjectTotals(record);
    //     return (
    //       <span style={{ color: pendingAmount > 0 ? "#fa541c" : "#52c41a" }}>
    //         {pendingAmount.toFixed(2)}
    //       </span>
    //     );
    //   },
    // },
    {
      title: "Status",
      align: "center",
      render: (_, record) => {
        const { status } = calculateProjectTotals(record);
        const color = status === "Paid" ? "success" : status === "Partially Paid" ? "warning" : "error";
        return <Badge status={color} text={status} />;
      },
    },
    {
      title: "Action",
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate("/project-expenses/edit", {
                state: { editBatch: record.data, project: record.project },
              })}
              disabled={!perm.edit}
            />
          </Tooltip>
          <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
            <Button type="primary" onClick={() => handlePayNow(record)} disabled={!perm.edit}>
              Pay Now
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Sub-table columns (inside expandable row)
  const subColumns = [
    { title: "S.No", render: (_, __, i) => i + 1, width: 60 },
    {
      title: "Item",
      dataIndex: "item_id",
      width: 160,
      render: (itemId) => {
        const name = itemMap[String(itemId)] || "-";
        return (
          <Tooltip title={name}>
            <div style={{
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160,
            }}>
              {name}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      width: 200,
      render: (text) => (
        <Tooltip title={text}>
          <div style={{
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200,
          }}>
            {text || "-"}
          </div>
        </Tooltip>
      ),
    },
    { title: "Qty", dataIndex: "qty", width: 80 },
    { title: "Unit Price", dataIndex: "unitprice", width: 100 },
    { title: "Total Price", dataIndex: "totalprice", width: 100 },
    { title: "Tax (%)", dataIndex: "tax", width: 80 },
    {
      title: "Tax Amount",
      render: (_, r) => {
        const price = Number(r.totalprice) || 0;
        const tax = Number(r.tax) || 0;
        return ((price * tax) / 100).toFixed(2);
      },
    },
    {
      title: "Final Amount",
      render: (_, r) => {
        const price = Number(r.totalprice) || 0;
        const tax = Number(r.tax) || 0;
        return (price + (price * tax) / 100).toFixed(2);
      },
    },
  ];

  const handleClearFilters = () => {
    setSearchText("");
    setDateRange([null, null]);
  };

  return (
    <>
      {contextHolder}

      {/* Payment Modal */}
      <Modal
        title={<Title level={4}>Pay Project Expense - {selectedProject?.project?.pro_no}</Title>}
        open={paymentModalOpen}
        onCancel={() => setPaymentModalOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        {selectedProject && (
          <ProExpensePay
            quotationNo={selectedProject?.project?.pro_no}
            grandTotal={projectGrandTotal}
            onPaidChange={(paid) => {
              setProjectPaidMap(prev => ({
                ...prev,
                [selectedProject.project.pro_no]: paid,
              }));
            }}
          />

        )}
      </Modal>

      <Card
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <ArrowLeftOutlined style={{ cursor: "pointer", fontSize: 18 }} onClick={() => navigate('/projects')} />
            <span style={{ fontWeight: 600, fontSize: 20 }}>Manage Project Expenses</span>
          </span>
        }
        extra={
          <Space>
            {/* <Button size="large" onClick={() => navigate("/projects/create")}>
              Create Project
            </Button> */}
            <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
              <Button type="primary" size="large" onClick={() => navigate("/project-expenses/create")} disabled={!perm.add}>
                Add Project Expense
              </Button>
            </Tooltip>
          </Space>
        }
      >
        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="Search Project Name, No., Description..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 400 }}
              size="large"
              allowClear
            />
            <RangePicker
              size="large"
              format="DD MMM YYYY"
              onChange={(dates) => {
                if (dates) {
                  setDateRange([
                    dates[0].startOf("day").toDate(),
                    dates[1].endOf("day").toDate(),
                  ]);
                } else {
                  setDateRange([null, null]);
                }
              }}
            />
            <Button size="large" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Space>
        </Card>

        {/* Project Expenses Table */}
        <Table
          rowKey={(r) => `${r.project.id}-${r.data?.[0]?.id || r.data?.[0]?.added_on || Math.random()}`}
          dataSource={filteredProjects}
          columns={projectColumns}
          pagination={{ pageSize: 10 }}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                dataSource={record.data}
                columns={subColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ),
          }}
        />
      </Card>

      {/* Add/Edit Drawer - full screen */}
      {/* <Drawer
        title="Create Project Expenses"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width="100%"
        styles={{ body: { padding: 24, background: "#f5f7fa" } }}
        closeIcon={<span style={{ fontSize: 18 }}>←</span>}
      >
        <CreateProEx onClose={() => setDrawerOpen(false)} getAllExpense={getAllExpense} />
      </Drawer> */}
    </>
  );
};

export default ManageProEx;